import isElectron from 'is-electron';
import React, { useEffect, useRef, useState } from 'react';
import Camera from '../../assets/icons/camera.svg';
import type { OptionItemProps } from '../Options/Options';
import Options from '../Options/Options';
import Gallery from '../../assets/imgs/chats/gallery.svg';
import Pin from '../../assets/imgs/chats/pin.svg';
import NewDoc from '../../assets/imgs/chats/new-doc.svg';
import SendIcon from '../../assets/icons/paper-plane--yellow.svg';
import { saveImageToServer } from '../../apis/mediaAPI';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
  selectMessageContent,
  setIsLoading,
  setMessageContentProperty,
  setReplyType,
  setSoundType,
  setSubject,
} from '../../containers/CreateMessage/createMessageSlice';
import { ReactComponent as PDF } from '../../assets/imgs/chats/pdf.svg';
import Delete from '../../assets/imgs/chats/delete-round.svg';
import { trunctateText } from '../../utils/truncate';
import { Wrapper } from '@googlemaps/react-wrapper';
import { Map } from '../Map/Map';
import { Marker } from '../Map/MapElements/MapMarker';
import { saveLocation } from '../../apis/locationAPI';
import MapDisplay from '../Map/MapDisplay';
import Geocoder, { apiKey } from '../../utils/geocoder';
import { ReactComponent as Edit } from '../../assets/imgs/chats/edit.svg';
import { ReactComponent as Close } from '../../assets/imgs/chats/close.svg';
import ReplyClose from '../../assets/imgs/chats/close-circle.svg';
import { translate } from '../../utils/translate';
import type { ReplyToMessageShorted } from '../../containers/Chat/Chat';
import { useTranslation } from 'react-i18next';
import { useLayoutContext } from '../../utils/customHooks/LayoutContext';
import HyperLinkText from '../HyperlinkText/HyperLinkText';
import ResizeObserver from 'resize-observer-polyfill';
import { setAreFilesSending } from '../../containers/ChatsList/chatListSlice';
import {
  SFileInput,
  SChatBoxWrapper,
  SChatBox,
  STop,
  SBottom,
  YellowAdd,
  SImageContainer,
  SImg,
  SPdf,
  SAddMoreImages,
  STextContainer,
  STextInput,
  SIcon,
  SAttachedMap,
  EditContainer,
  SReplyContainer,
  SReplyTextContainer,
  SFakeFooter,
} from './ChatBox.styles';
import { useConfirmation } from '../../utils/ConfirmationServiceContext/confirmationContext';
import { acceptedImageUploadFormatsText } from '../../utils/global';
import { getUnsupportedImageUploadWarningText } from '../../utils/helperFunctions';
import type { FilePreview } from '../../containers/CreateMessage/createMessageSlice/types';
import { CreateMessageReplyType, SoundType } from '../../utils/enums';
import { shouldUseMapAccuracy } from '../../utils/features';
import { Circle as MapCircle } from '../Map/MapElements/MapCircle';

type ChatBoxProps = {
  editText?: string;
  tabBar: boolean;
  setTabBar: React.Dispatch<React.SetStateAction<boolean>>;
  setEditText?: React.Dispatch<React.SetStateAction<string | undefined>>;
  className?: string;

  isDependencyAdded?: boolean;
  dependencyRequired?: boolean;
  onDependencyReFocus?: () => void;

  replyMessage?: ReplyToMessageShorted;
  dismissReplyToMessage?: () => void;

  onTextSend: (textMessage: string) => void;
  onPhotosSend: (photosList: string[], textMessage: string) => void;
  onDocumentsSend: (documentFiles: File[], textMessage: string) => void;
  onLocationSend: (locationId: number, text?: string) => void;
  hideGalleryOption?: boolean;
  hideLocationOption?: boolean;
  hideFileOption?: boolean;
};

function ChatBox(props: ChatBoxProps) {
  const {
    editText,
    className,
    setEditText,
    onTextSend,
    onPhotosSend,
    onDocumentsSend,
    onLocationSend,
    isDependencyAdded,
    dependencyRequired,
    onDependencyReFocus,
    replyMessage,
    dismissReplyToMessage,
    hideGalleryOption = false,
    hideLocationOption = false,
    hideFileOption = false,
  } = props;

  const dispatch = useAppDispatch();
  const layout = useLayoutContext();
  const { t } = useTranslation();
  const confirm = useConfirmation();

  const messageContent = useAppSelector(selectMessageContent);
  const { text, images, documents, location } = messageContent;
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [keyReload, setKeyReload] = useState(0);
  const [mapLocation, setMapLocation] = useState<
    | (google.maps.LatLngLiteral & {
        accuracy?: number;
      })
    | null
    | undefined
  >(location);
  const [fakeFooterHeight, setFakeFooterHeight] = useState(0);

  const fileUploadRef = useRef<any>(null);
  const imageUploadRef = useRef<any>(null);
  const imageContainerRef = useRef<any>(null);
  const mapCircleRef = useRef<google.maps.Circle>(null);
  const chatBoxElRef = useRef(null);
  const showSendButton =
    !!text?.trim()?.length || !!documents.length || !!images.length || !!location;
  const removeAttachmentsButton = !!images.length || !!documents.length || !!location;

  const updateFakeFooterHeight = () => {
    if (chatBoxElRef.current) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setFakeFooterHeight(chatBoxElRef.current.clientHeight);
    }
  };

  useEffect(() => {
    if (isElectron()) {
      // Initial update fakeFooter height
      updateFakeFooterHeight();

      // Track chatBox height changes
      const resizeObserver = new ResizeObserver(updateFakeFooterHeight);
      if (chatBoxElRef.current) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        resizeObserver.observe(chatBoxElRef.current);
      }

      // Cleanup
      return () => resizeObserver.disconnect();
    }
  }, [layout.isMapOpen]);

  useEffect(() => {
    if (!shouldUseMapAccuracy) return;

    if (!location?.accuracy) return;

    setTimeout(() => {
      const circleBounds = mapCircleRef.current?.getBounds();
      const map = mapCircleRef.current?.getMap();
      if (map && circleBounds) map.fitBounds(circleBounds, 40);
    }, 100);
  }, [location, mapCircleRef]);

  const textInputRef = useRef<HTMLTextAreaElement>(null);
  const limitedSize = location ? 69 : !!images.length || !!documents.length ? 115 : 186;

  const handleTextInputResize = () => {
    if (
      textInputRef.current !== undefined &&
      textInputRef.current !== null &&
      textInputRef.current.style !== undefined &&
      textInputRef.current.style.height !== undefined
    ) {
      textInputRef.current.style.height = '2.5rem';

      if (textInputRef.current.scrollHeight >= limitedSize) {
        textInputRef.current.style.height = `${limitedSize}px`;
        textInputRef.current.style.borderRadius = `${20}px`;
      } else if (textInputRef.current.scrollHeight !== undefined) {
        textInputRef.current.style.height = `${textInputRef.current.scrollHeight}px`;
      }
    }
  };

  const onCancelImages = () => {
    dispatch(setMessageContentProperty({ key: 'images', value: [] }));
    if (imageUploadRef.current) imageUploadRef.current.value = null;
    handleTextInputResize();
  };

  const onCancelDocuments = () => {
    dispatch(setMessageContentProperty({ key: 'documents', value: [] }));
    if (fileUploadRef.current) fileUploadRef.current.value = null;
    handleTextInputResize();
  };

  const onCancelLocation = () => {
    setMapLocation(null);
    dispatch(setMessageContentProperty({ key: 'location', value: null }));
  };

  useEffect(() => {
    if (editText) {
      onCancelImages();
      onCancelDocuments();
      onCancelLocation();
      dispatch(setMessageContentProperty({ key: 'text', value: editText }));
    }
  }, [editText]);

  const onImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const imagesUploaded = Array.prototype.slice.call(e.target.files);
    if (!imagesUploaded.length) return;

    const newImages = [...images];

    imagesUploaded.forEach((img, index) => {
      const path = URL.createObjectURL(img);
      newImages.push({
        name: img.name,
        path,
        id: images.length + index,
      });
    });

    dispatch(setMessageContentProperty({ key: 'images', value: newImages }));
    setOptionsOpen(false);
  };

  const onFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const documentsUploaded = Array.prototype.slice.call(e.target.files);
    if (!documentsUploaded.length) return;

    const newDocuments = [...documents];
    documentsUploaded.forEach((doc, index) => {
      const path = URL.createObjectURL(doc);
      newDocuments.push({
        name: doc.name,
        path,
        id: documents.length + index,
      });
    });

    dispatch(setMessageContentProperty({ key: 'documents', value: newDocuments }));
    setOptionsOpen(false);
  };

  const options: OptionItemProps[] = [];
  if (!hideGalleryOption)
    options.push({
      name: 'messages_photo_or_video',
      icon: Gallery,
      callback: () => {
        imageUploadRef?.current?.click();
      },
    });
  if (!hideLocationOption)
    options.push({
      name: 'messages_location',
      icon: Pin,
      callback: () => {
        layout.setMapOpen(true);
        setOptionsOpen(false);
      },
    });
  if (!hideFileOption)
    options.push({
      name: 'messages_file',
      icon: NewDoc,
      callback: () => {
        fileUploadRef?.current?.click();
      },
    });

  const onClickAdd = () => {
    setOptionsOpen(!optionsOpen);
  };

  const saveImage = async (image: FilePreview) => {
    try {
      const imageResponse = await fetch(image.path);
      const imageBlob = await imageResponse.blob();
      const file = new File([imageBlob], image.name, { type: imageBlob.type });
      const formData = new FormData();
      formData.append('image', file);
      const result = await saveImageToServer(formData);
      if (result) {
        return result;
      }
    } catch (error) {
      console.error(error);
    }
  };

  const saveImages = async () => {
    const imagesFilesNamesArray = [];

    for await (const image of images) {
      const result = await saveImage(image);
      if (result) {
        imagesFilesNamesArray.push(result);
      }
    }
    return imagesFilesNamesArray;
  };

  const createDocumentFiles = async () => {
    const documentsList: File[] = [];

    for await (const doc of documents) {
      const docResponse = await fetch(doc.path);
      const docBlob = await docResponse.blob();
      const file = new File([docBlob], doc.name, { type: docBlob.type });
      documentsList.push(file);
    }
    return documentsList;
  };

  const sendMessage = async () => {
    if (images.length) {
      dispatch(setAreFilesSending(true));
      const imagesFilesNamesArray = await saveImages();
      if (!imagesFilesNamesArray?.length) {
        confirm({
          title: 'unsupported_image_format',
          description: getUnsupportedImageUploadWarningText(),
          onSubmit: () => {},
          confirmText: 'ok',
          confirmStyle: 'red',
        });
        dispatch(setAreFilesSending(false));
        dispatch(setIsLoading(false));
        return;
      }
      onPhotosSend(imagesFilesNamesArray, text);
    } else if (documents.length) {
      dispatch(setAreFilesSending(true));
      const documentsList = await createDocumentFiles();
      onDocumentsSend(documentsList, text);
    } else if (location) {
      dispatch(setAreFilesSending(true));
      const result = await Geocoder({
        latitude: location.lat,
        longitude: location.lng,
      });
      const locName = result?.addressName
        ? result?.addressName
        : `${location.lat} | ${location.lng}`;
      const loc = {
        name: locName,
        latitude: location.lat,
        longitude: location.lng,
        accuracy: location.accuracy,
      };
      const createLocationResponse = await saveLocation(loc);
      onLocationSend(createLocationResponse.id, text);
    } else {
      onTextSend(text);
    }
  };

  const resetTextInputResize = () => {
    if (
      textInputRef.current !== undefined &&
      textInputRef.current !== null &&
      textInputRef.current.style !== undefined &&
      textInputRef.current.style.height !== undefined
    ) {
      textInputRef.current.style.height = '2.5rem';
    }
  };

  const onCancelEdit = () => {
    if (setEditText) {
      setEditText(undefined);
    }
    dispatch(setMessageContentProperty({ key: 'text', value: '' }));
    resetTextInputResize();
  };

  const clearOnSend = () => {
    dispatch(setSubject(''));
    dispatch(setSoundType(SoundType.Regular));
    dispatch(setReplyType(CreateMessageReplyType.replyAll));
    onCancelImages();
    onCancelDocuments();
    onCancelLocation();
    onCancelEdit();
  };

  const onMessageSend = async () => {
    if (dependencyRequired) {
      if (isDependencyAdded) {
        dispatch(setIsLoading(true));
        sendMessage();
        clearOnSend();
      } else if (onDependencyReFocus) {
        onDependencyReFocus();
      }
    } else {
      dispatch(setIsLoading(true));
      sendMessage();
      clearOnSend();
    }
  };

  const onWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const containerScrollPosition = imageContainerRef.current.scrollLeft;
    imageContainerRef.current.scrollTo({
      top: 0,
      left: containerScrollPosition + e.deltaY * 0.1,
    });
  };

  const handleRemoveImage = (id: number) => {
    const newImagesList = [...images].filter(img => img.id !== id);
    dispatch(setMessageContentProperty({ key: 'images', value: newImagesList }));
    setKeyReload(keyReload + 1);

    if (!newImagesList.length) {
      onCancelImages();
    }
  };

  const handleRemoveDocument = (id: number) => {
    const newDocumentsList = [...documents].filter(img => img.id !== id);
    dispatch(setMessageContentProperty({ key: 'documents', value: newDocumentsList }));
    setKeyReload(keyReload + 1);

    if (!newDocumentsList.length) {
      onCancelDocuments();
    }
  };

  const removeAttachments = () => {
    if (documents.length) onCancelDocuments();
    else if (location) onCancelLocation();
    else onCancelImages();
  };

  useEffect(() => {
    if (textInputRef.current) handleTextInputResize();
    if (!textInputRef.current) setTimeout(handleTextInputResize, 5);
  }, [optionsOpen, layout.isMapOpen]);

  if (layout.isMapOpen) {
    return (
      <MapDisplay
        backLink="#"
        handleClickBack={() => layout.setMapOpen(false)}
        buttonColor="yellow"
        buttonTx="messages_attach_map"
        location={mapLocation ?? undefined}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        setLocation={setMapLocation}
        onButtonClick={() => {
          layout.setMapOpen(false);
          dispatch(setMessageContentProperty({ key: 'location', value: mapLocation }));
        }}
      />
    );
  }

  return (
    <>
      {props.tabBar && (
        <>
          {editText && (
            <EditContainer role="editContainer">
              <Edit role="editIcon" />
              <p className="editText" role="editText">
                {translate('message_editing')}
              </p>
              <Close onClick={onCancelEdit} className="close" role="close" />
            </EditContainer>
          )}
          {replyMessage && (
            <SReplyContainer role="replyContainer">
              {replyMessage.messageIcon && (
                <img
                  role="messageIcon"
                  src={replyMessage.messageIcon.src}
                  alt={'messageIcon'}
                  className={replyMessage.messageIcon.classPresets}
                />
              )}
              <SReplyTextContainer role="replyTextContainer">
                <p className="senderTitle" role="senderTitle">
                  {t('message_reply_to', { sender: replyMessage.senderName })}
                </p>
                <p className="messageBody" role="messageBody">
                  <HyperLinkText text={replyMessage.messageText} />
                </p>
              </SReplyTextContainer>
              <img
                role="closeIcon"
                src={ReplyClose}
                alt={'close'}
                onClick={dismissReplyToMessage}
                className="close"
              />
            </SReplyContainer>
          )}
          <SChatBoxWrapper
            ref={chatBoxElRef}
            className={className}
            map={!!location}
            margin={!!images.length || !!documents.length}
            role="chatBoxWrapper"
          >
            <SChatBox role="chatBox">
              <STop role="contentContainer">
                {removeAttachmentsButton ? (
                  <SIcon onClick={removeAttachments} src={Delete} alt="Delete" role="deleteIcon" />
                ) : options.length ? (
                  <YellowAdd
                    role="addIcon"
                    onClick={editText ? () => {} : onClickAdd}
                    $disable={!!editText}
                  />
                ) : null}
                <STextContainer
                  role="textContainer"
                  onSubmit={(e: React.FormEvent<HTMLFormElement>) => e.preventDefault()}
                >
                  <STextInput
                    role="textInput"
                    ref={textInputRef}
                    value={text}
                    placeholder={translate('messages_writeYourMessage')}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                      handleTextInputResize();
                      dispatch(setMessageContentProperty({ key: 'text', value: e.target.value }));
                    }}
                  />
                </STextContainer>
                {showSendButton ? (
                  <SIcon role="sendIcon" onClick={onMessageSend} src={SendIcon} alt="send" />
                ) : !hideGalleryOption ? (
                  <SIcon
                    onClick={() => imageUploadRef?.current?.click()}
                    src={Camera}
                    alt="camera"
                    role="cameraIcon"
                  />
                ) : null}
              </STop>
              {!!images.length && (
                <SBottom role="imagesRow">
                  <SImageContainer onWheel={onWheel} ref={imageContainerRef} role="imagesContainer">
                    <div className="scrollableImages" key={keyReload} role="scrollableImages">
                      {images.map(image => (
                        <SImg key={image.id} role="imageContainer">
                          <img role="image" className="picture" src={image.path} alt="" />
                          <img
                            className="del"
                            src={Delete}
                            alt=""
                            onClick={() => handleRemoveImage(image.id)}
                            role="deleteIcon"
                          />
                        </SImg>
                      ))}
                    </div>
                  </SImageContainer>
                  <SAddMoreImages
                    role="addMoreImages"
                    onClick={() => imageUploadRef?.current?.click()}
                  >
                    <YellowAdd role="add" />
                  </SAddMoreImages>
                </SBottom>
              )}
              {!!documents.length && (
                <SBottom role="documentsRow">
                  <SImageContainer
                    pdf={true}
                    onWheel={onWheel}
                    ref={imageContainerRef}
                    role="documentsContainer"
                  >
                    <div className="scrollableImages" role="scrollableDocuments">
                      {documents.map((doc, key) => (
                        <SPdf key={key} role="pdfContainer">
                          <PDF role="pdfIcon" />
                          <img
                            className="del"
                            src={Delete}
                            alt=""
                            onClick={() => {
                              handleRemoveDocument(doc.id);
                            }}
                            role="deleteIcon"
                          />
                          <p role="documentName">{trunctateText(doc.name, 20)}</p>
                        </SPdf>
                      ))}
                    </div>
                  </SImageContainer>
                  <SAddMoreImages
                    role="addMoreImages"
                    onClick={() => fileUploadRef?.current?.click()}
                  >
                    <YellowAdd role="add" />
                  </SAddMoreImages>
                </SBottom>
              )}
              {location && (
                <SAttachedMap role="attachedMap">
                  <Wrapper apiKey={apiKey!} libraries={['places']}>
                    <Map
                      center={location}
                      zoom={18}
                      className="fullHeightMap"
                      disableDefaultUI
                      clickableIcons={false}
                    >
                      {shouldUseMapAccuracy && !!location.accuracy && (
                        <MapCircle
                          ref={mapCircleRef}
                          center={{ lat: location.lat, lng: location.lng }}
                          radius={location.accuracy}
                        />
                      )}
                      <Marker position={location} />
                    </Map>
                  </Wrapper>
                </SAttachedMap>
              )}
            </SChatBox>
          </SChatBoxWrapper>
        </>
      )}
      <Options items={options} isOpen={optionsOpen} setIsOpen={setOptionsOpen} />
      <SFileInput
        onChange={onImageUpload}
        ref={imageUploadRef}
        id="imageUpload"
        type="file"
        multiple
        accept={acceptedImageUploadFormatsText}
      />
      <SFileInput
        onChange={onFileUpload}
        ref={fileUploadRef}
        id="fileUpload"
        type="file"
        multiple
        accept="application/pdf"
      />
      {isElectron() && (
        <SFakeFooter
          role="fakeFooter"
          $height={fakeFooterHeight ? `${fakeFooterHeight}px` : undefined}
        ></SFakeFooter>
      )}
    </>
  );
}

export default ChatBox;
